import { Button, ConfirmDialog, Spinner } from "@omony/ui";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { api } from "../api.js";
import { useHashRoute } from "../hashRoute.js";
import { refreshSession, useSession, type SessionInfo } from "../query.js";
import {
  ForgotScreen,
  InvitationScreen,
  LoginScreen,
  logout,
  MfaSetupScreen,
  MfaVerifyScreen,
  PasswordChangeScreen,
  ResetScreen,
  WrongRoleScreen,
} from "./screens.js";
import styles from "./auth.module.css";

/**
 * Steuert die Anmeldeschritte: Einladung / Reset (Token im Fragment) → Login → MFA (Einrichtung oder Code)
 * → Pflicht-Passwortwechsel → App. Erst danach werden die Kinder gerendert.
 */
export function AuthGate({
  appRole: role,
  appTitleId,
  loginExtra,
  children,
}: {
  appRole: SessionInfo["role"];
  appTitleId: string;
  /** Zusätzlicher Inhalt unter dem Login (z. B. „Gerät koppeln“ in der Konsole) */
  loginExtra?: ReactNode;
  children: (s: SessionInfo) => ReactNode;
}) {
  const intl = useIntl();
  const appTitle = intl.formatMessage({ id: appTitleId });
  const [route] = useHashRoute();
  const session = useSession();

  if (route[0] === "einladung" && route[1]) return <InvitationScreen appTitle={appTitle} token={route[1]} />;
  if (route[0] === "passwort-neu" && route[1]) return <ResetScreen appTitle={appTitle} token={route[1]} />;
  if (route[0] === "passwort-vergessen") return <ForgotScreen appTitle={appTitle} />;

  if (session.isLoading) {
    return (
      <div className={styles.page}>
        <Spinner label={intl.formatMessage({ id: "common.loading" })} size={32} />
      </div>
    );
  }
  const s = session.data?.session;
  if (!session.data?.authenticated || !s) return <LoginScreen appTitle={appTitle} extra={loginExtra} />;
  if (s.kind === "account" && s.role !== role) return <WrongRoleScreen appTitle={appTitle} />;
  if (s.mfa.state === "ausstehend")
    return s.mfa.configured ? (
      <MfaVerifyScreen appTitle={appTitle} />
    ) : (
      <MfaSetupScreen appTitle={appTitle} />
    );
  if (s.mustChangePassword) return <PasswordChangeScreen appTitle={appTitle} forced />;
  return (
    <>
      <IdleWatcher idleSeconds={s.idleTimeoutSeconds} />
      {children(s)}
    </>
  );
}

const WARN_SECONDS = 120;

/**
 * Laufendes Videogespräch: Eingaben landen im Video-iFrame, nicht auf der Seite. Solange ein
 * Halt aktiv ist, gilt die Nutzerin / der Nutzer als aktiv (Keepalive läuft weiter).
 */
let holds = 0;
export function useSessionHold(active: boolean) {
  useEffect(() => {
    if (!active) return;
    holds++;
    return () => {
      holds--;
    };
  }, [active]);
}

/**
 * Idle-Timeout (ARCHITEKTUR §8.2): Nutzeraktivität hält die Sitzung per Keepalive (max. 1×/min) aktiv.
 * Ohne Aktivität erscheint 2 Minuten vor Ablauf ein Dialog mit Countdown.
 */
function IdleWatcher({ idleSeconds }: { idleSeconds: number }) {
  const intl = useIntl();
  const lastServer = useRef(0);
  const lastActivity = useRef(0);
  const [remaining, setRemaining] = useState<number | null>(null);

  useEffect(() => {
    lastServer.current = Date.now();
    lastActivity.current = Date.now();
    const onActivity = () => {
      lastActivity.current = Date.now();
    };
    const events = ["pointerdown", "keydown", "wheel", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const id = window.setInterval(() => {
      const now = Date.now();
      if (holds > 0) lastActivity.current = now;
      const activeRecently = now - lastActivity.current < 60_000;
      if (activeRecently && now - lastServer.current > 60_000) {
        lastServer.current = now;
        void api("/auth/keepalive", { method: "POST" }).catch(() => refreshSession());
      }
      const left = Math.round(idleSeconds - (now - lastServer.current) / 1000);
      if (left <= 0) {
        setRemaining(null);
        void refreshSession();
      } else setRemaining(left <= WARN_SECONDS && !activeRecently ? left : null);
    }, 1000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      window.clearInterval(id);
    };
  }, [idleSeconds]);

  return (
    <ConfirmDialog
      isOpen={remaining !== null}
      onOpenChange={(open) => {
        if (!open) setRemaining(null);
      }}
      title={intl.formatMessage({ id: "auth.idle.title" })}
      confirmLabel={intl.formatMessage({ id: "auth.idle.stay" })}
      onConfirm={() => {
        lastServer.current = Date.now();
        lastActivity.current = Date.now();
        void api("/auth/keepalive", { method: "POST" }).catch(() => refreshSession());
      }}
    >
      {intl.formatMessage({ id: "auth.idle.text" }, { seconds: remaining ?? 0 })}
    </ConfirmDialog>
  );
}

export function LogoutButton() {
  const intl = useIntl();
  return (
    <Button variant="quiet" onPress={() => void logout()}>
      {intl.formatMessage({ id: "auth.logout" })}
    </Button>
  );
}
